import { HttpClient, HttpResponse } from "@angular/common/http";
import { Invoice } from "../models/invoice.model";
import { SalesRep } from "../models/sales-rep.model";
import { BACKEND_URI } from "../config/config-vars";
import jsPDF from "jspdf";
import { Injectable } from "@angular/core";
import { firstValueFrom, Observable, Subscription } from "rxjs";
import { LoginService } from "./login.service";
import { Payment } from "../models/payment.model";

const BASE_URL = `${BACKEND_URI}/api/invoices`;
const SEND_INVOICE_EMAIL = `${BASE_URL}/send`;
const PAYMENT_URL = `${BASE_URL}/payments`;
const PAYMENT_SALESREP_URL =  (id) =>`${BASE_URL}/payments/${id}`;
const GET_ALL_URL = BASE_URL;


@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private invoices: Invoice[] = [];

  constructor(private http: HttpClient, private loginService: LoginService) { }

  hasBeenInvoiced(salesRep: SalesRep): boolean {
    if(salesRep.lastInvoice) {
      const dueDate =  salesRep.lastInvoice?.dueDate as unknown as string;
      const thisPeriodDueDate = this.calculateDueDate().toISOString().substring(0, 10);

      return dueDate >= thisPeriodDueDate;
    }

    return false;
  }

  // Generate invoices and send PDFs
  generateInvoice(salesRep: SalesRep): Invoice {
    const invoice: Invoice = {
      invoiceNumber: this.generateUniqueId(),
      salesRep,
      amount: salesRep.monthlyPayment, // Ensure this is a number, not undefined
      issueDate: new Date(),
      dueDate: this.calculateDueDate(),
      status: 'pending',
      id: ""
    };

    // Generate the PDF after creating the invoice
    this.generateInvoicePDF(invoice);

    return {
      ...invoice,
    };
  }



  private generateUniqueId(): string {
    const numericId = Math.floor(Math.random() * 1000000); // Generate a random number up to 999999
    return `FG${numericId}`;
  }

  // Calculate default due date (next month)
  private calculateDueDate(): Date {
    const dueDate = new Date();

    // Move to the next month
    dueDate.setMonth(dueDate.getMonth() + 1);

    // Set the date to the 5th
    dueDate.setDate(5);
    dueDate.setHours(0);
    dueDate.setMinutes(0);
    dueDate.setSeconds(0);
    dueDate.setMilliseconds(0);

    return dueDate;
  }

  // Generate the invoice PDF
  async generateInvoicePDF(invoice: Invoice): Promise<void> {
    const doc = new jsPDF();

    // Add Company Logo
    const img = new Image();
    img.src = 'assets/images/logo-sm-dark.png';
    doc.addImage(img, 'PNG', 14, 10, 40, 20);

    // Company Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Fortune Group Games Inc.', 140, 20);
    doc.setFont('helvetica', 'normal');
    doc.text('5675 Jimmy Carter Blvd STE 725', 140, 26);
    doc.text('Norcross, GA 30071', 140, 32);
    doc.text('Phone: +1 (833) 387-4067', 140, 38);
    doc.text('Email: billing@fusiongroupgames.com', 140, 44);

    // Invoice Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', 105, 60, { align: 'center' });

    // Invoice Details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${invoice.invoiceNumber}`, 14, 75);
    doc.text(`Issue Date: ${invoice.issueDate.toLocaleDateString()}`, 14, 82);
    doc.text(`Due Date: ${invoice.dueDate.toLocaleDateString()}`, 14, 89);

    // Sales Rep Details
    doc.setFont('helvetica', 'bold');
    doc.text('Billed To:', 14, 105);
    doc.setFont('helvetica', 'normal');
    doc.text(`${invoice.salesRep.firstName} ${invoice.salesRep.lastName}`, 14, 112);
    doc.text(`Email: ${invoice.salesRep.primaryEmail}`, 14, 119);

    // Table Header
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 14, 140);
    doc.text('Qty', 100, 140);
    doc.text('Rate', 120, 140);
    doc.text('Amount', 160, 140);
    doc.line(14, 142, 195, 142);

    // Table Content
    doc.setFont('helvetica', 'normal');
    doc.text('Monthly Subscription', 14, 150);
    doc.text(`${invoice.salesRep.numBoards}`, 100, 150);
    doc.text(`$${invoice.amount}`, 120, 150);
    doc.text(`$${invoice.amount}`, 160, 150);

    // Payment Summary
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 140, 180);
    doc.text(`$${invoice.amount}`, 160, 180);    

    
    doc.text('Total:', 140, 194);
    const total = invoice.amount.toFixed(2);
    doc.text(`$${total}`, 160, 194);

    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Please make payment to the account before the due date.', 14, 220);

    // Late Fee Information
    doc.text(`Late Fee of 10% added if paid after ${invoice.dueDate.toLocaleDateString()}`, 14, 230);

    // Calculate late fee
    const lateFee = (invoice.amount * 0.1).toFixed(2);
    doc.text(`Late Fee: $${lateFee}`, 14, 240);

    // Total including Late Fee
    const lateFeeTotal = (invoice.amount + parseFloat(lateFee)).toFixed(2);
    doc.text(`Total with Late Fee: $${lateFeeTotal}`, 14, 250);

    // Bank details
    doc.text('Bank: Wells Fargo Bank | Account No: 2699721516 | Routing No: 061000227', 14, 260);

    // Thank you note
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for your business!', 14, 270);

    // Save the invoice to the backend
    try {
      const savedInvoice = await this.create(invoice);  // ✅ Corrected
      console.log('Invoice saved:', savedInvoice);

      // Save the PDF after saving the invoice
      const pdfFileName = `invoice_${invoice.salesRep.firstName}_${invoice.salesRep.lastName}_${invoice.issueDate.toLocaleDateString()}.pdf`;
      doc.save(pdfFileName);

      console.log('Invoice PDF generated and saved.');
    } catch (error) {
      console.error('Error saving invoice:', error);
    }
  }


  // Get all invoices
  getInvoices(): Invoice[] {
    return this.invoices;
  }

  generateAndSendInvoicePDF(invoice: Invoice): void {
    console.log('Generating invoice for:', invoice);

    const doc = new jsPDF();

    // Add Company Logo
    const img = new Image();
    img.src = 'assets/images/logo-sm-dark.png'; // Correct path with forward slashes
    doc.addImage(img, 'PNG', 14, 10, 40, 20);

    // Company Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Fortune Group Games Inc.', 140, 20);
    doc.setFont('helvetica', 'normal');
    doc.text('5675 Jimmy Carter Blvd STE 725', 140, 26);
    doc.text('Norcross, GA 30071', 140, 32);
    doc.text('Phone: +1 (833) 387-4067', 140, 38);
    doc.text('Email: billing@fusiongroupgames.com', 140, 44);

    // Invoice Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', 105, 60, { align: 'center' });

    // Invoice Details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${invoice.invoiceNumber}`, 14, 75);
    doc.text(`Issue Date: ${invoice.issueDate.toLocaleDateString()}`, 14, 82);
    doc.text(`Due Date: ${invoice.dueDate.toLocaleDateString()}`, 14, 89);

    // Sales Rep Details
    doc.setFont('helvetica', 'bold');
    doc.text('Billed To:', 14, 105);
    doc.setFont('helvetica', 'normal');
    doc.text(`${invoice.salesRep.firstName} ${invoice.salesRep.lastName}`, 14, 112);
    doc.text(`Email: ${invoice.salesRep.primaryEmail}`, 14, 119);

    // Table Header
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 14, 140);
    doc.text('Qty', 100, 140);
    doc.text('Rate', 120, 140);
    doc.text('Amount', 160, 140);
    doc.line(14, 142, 195, 142);

    // Table Content
    doc.setFont('helvetica', 'normal');
    doc.text('Monthly Subscription', 14, 150);
    doc.text(`${invoice.salesRep.numBoards}`, 100, 150);
    doc.text(`$${invoice.amount}`, 120, 150);
    doc.text(`$${invoice.amount}`, 160, 150);

    // Payment Summary
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 140, 180);
    doc.text(`$${invoice.amount}`, 160, 180);

    doc.text('Total:', 140, 194);
    const total = (invoice.amount).toFixed(2);
    doc.text(`$${total}`, 160, 194);

    // Footer with Payment Instructions and Late Fee Information
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Please make payment to the account before the due date.', 14, 220);

    // Late Fee Information
    doc.text(`Late Fee of 10% added if paid after ${invoice.dueDate.toLocaleDateString()}`, 14, 230);

    // Calculate late fee
    const lateFee = (invoice.amount * 0.1).toFixed(2);
    doc.text(`Late Fee: $${lateFee}`, 14, 240);

    // Total including Late Fee
    const lateFeeTotal = (invoice.amount + parseFloat(lateFee)).toFixed(2);
    doc.text(`Total with Late Fee: $${lateFeeTotal}`, 14, 250);

    // Bank details
    doc.text('Bank: Wells Fargo Bank | Account No: 2699721516 | Routing No: 061000227', 14, 260);

    // Thank you note
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for your business!', 14, 270);

    // Prepare the PDF Blob
    const pdfBlob = doc.output('blob');

    // Check if an existing invoice already exists with the same salesRep, amount, and dueDate
    this.create(invoice).then((savedInvoice: Invoice) => {
    }).catch(error => {
      console.error('Error creating or updating invoice:', error);
    });
  }

  // Safe method to format the amount
  private getFormattedAmount(amount: number): string {
    if (isNaN(amount) || amount == null) {
      return '$0.00'; // Return $0.00 if the amount is invalid
    }
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  async sendExistingInvoiceEmail(invoiceId: string): Promise<Invoice> {
    if (this.loginService.isloggedIn) {
      return await firstValueFrom(
        this.http.post<Invoice>(`${BASE_URL}/send/${invoiceId}`, {})
      );
    }
  }
  

  async create(data: Invoice): Promise<Invoice> {
    if (this.loginService.isloggedIn) {
      return await firstValueFrom(
        this.http.post<Invoice>(`${BASE_URL}`, data)
      );
    }
  }

  async getAll(): Promise<Invoice[]> {
      if(this.loginService.isloggedIn) {
          return await firstValueFrom(
              this.http.get<Invoice[]>(GET_ALL_URL)
          );
      }
  }
}


